/** admin/sessions — HOOKS. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { moderationApi } from '../../moderator/moderation/api';
import { sessionsApi } from './api';
import { nextInterruptionCheck, runningWorlds } from './helpers';
import { adminSessionsKeys } from './keys';
import type { CreateWorldPayload, InterruptionCheck, UpdateWorldPayload, UpsertSessionPayload, WorldItem } from './types';

const invalidateWorlds = (qc: QueryClient) => qc.invalidateQueries({ queryKey: adminSessionsKeys.worldsAll() });
function invalidateSessions(qc: QueryClient, gameName?: string, worldName?: string) {
  if (gameName && worldName) qc.invalidateQueries({ queryKey: adminSessionsKeys.sessions(gameName, worldName) });
  else qc.invalidateQueries({ queryKey: adminSessionsKeys.sessionsAll() });
}

export function useAdminGameList(enabled = true) {
  return useQuery({ queryKey: adminSessionsKeys.games(), queryFn: () => sessionsApi.listGames(), enabled });
}

export function useAdminLocalSaves(enabled = true) {
  return useQuery({ queryKey: adminSessionsKeys.localSaves(), queryFn: () => sessionsApi.listLocalSaves(), enabled });
}

export function useAdminScripts(enabled = true) {
  return useQuery({ queryKey: adminSessionsKeys.scripts(), queryFn: () => sessionsApi.listSessionScripts(), enabled });
}

export function useAdminWorlds(page = 1, limit = 100, enabled = true) {
  return useQuery({ queryKey: adminSessionsKeys.worlds(page, limit), queryFn: () => sessionsApi.listWorlds(page, limit), enabled });
}

export function useAdminSessions(gameName: string | null, worldName: string | null) {
  return useQuery({
    queryKey: gameName && worldName ? adminSessionsKeys.sessions(gameName, worldName) : adminSessionsKeys.sessions('none', 'none'),
    queryFn: () => sessionsApi.listSessions(gameName!, worldName!),
    enabled: !!gameName && !!worldName,
  });
}

/** How often a world's running sessions are checked against the matches the game server runs. */
const INTERRUPTION_CHECK_MS = 10_000;

/**
 * How long a check waits, after reading the sessions, to read the matches.
 * Nakama lists a match from its label index, which holds a new match up to
 * label_update_interval_ms (a second) after it is made, while the scheduler
 * marks the session running at once: read sooner, a session the scheduler had
 * just started would be read beside a list without its match.
 */
const MATCH_LISTING_LAG_MS = 1_500;

/** No session interrupted: what a check that cannot say gives. */
const NONE_INTERRUPTED: ReadonlySet<string> = new Set();

/**
 * The ids of a world's running sessions whose match is gone: the world's
 * running sessions checked against the matches the game server runs
 * (nextInterruptionCheck), every INTERRUPTION_CHECK_MS while `enabled` — while
 * the screen shows one of them running. Each check reads the sessions afresh
 * and puts them in the world's list, so the sessions screen's cards say the
 * statuses it judged; a check that fails says nothing, and none is
 * interrupted. The sessions screen and a campaign's scheduler both ask it.
 */
export function useInterruptedSessionIds(gameName: string | null, worldName: string | null, enabled: boolean): ReadonlySet<string> {
  const qc = useQueryClient();
  const key = adminSessionsKeys.interruption(gameName ?? 'none', worldName ?? 'none');
  const check = useQuery({
    queryKey: key,
    enabled: enabled && !!gameName && !!worldName,
    refetchInterval: INTERRUPTION_CHECK_MS,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<InterruptionCheck> => {
      const sessions = await sessionsApi.listSessions(gameName!, worldName!);
      qc.setQueryData(adminSessionsKeys.sessions(gameName!, worldName!), sessions);
      await new Promise((resolve) => setTimeout(resolve, MATCH_LISTING_LAG_MS));
      const running = runningWorlds(await moderationApi.listMatches());
      // The check before counts while it is a poll or two old, a failed one between.
      return nextInterruptionCheck(qc.getQueryData<InterruptionCheck>(key), sessions, running, Date.now(), 2.5 * INTERRUPTION_CHECK_MS);
    },
  });
  return check.isSuccess ? check.data.interrupted : NONE_INTERRUPTED;
}

export function useCreateWorld() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: CreateWorldPayload) => sessionsApi.createWorld(payload), onSuccess: () => invalidateWorlds(qc) });
}

export function useUpdateWorld() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; payload: UpdateWorldPayload }) => sessionsApi.updateWorld(v.id, v.payload), onSuccess: () => invalidateWorlds(qc) });
}

export function useDeleteWorld() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => sessionsApi.deleteWorld(id), onSuccess: () => invalidateWorlds(qc) });
}

/**
 * Resets a world's save. Its running sessions' matches end with it, so the
 * world's session list and its interruption check are read again.
 */
export function useResetWorldSave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (world: WorldItem) => sessionsApi.resetWorldSave(world.id),
    onSuccess: (_d, world) => {
      invalidateSessions(qc, world.gameName, world.name);
      qc.invalidateQueries({ queryKey: adminSessionsKeys.interruption(world.gameName, world.name) });
    },
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: UpsertSessionPayload) => sessionsApi.createSession(payload), onSuccess: (_d, p) => invalidateSessions(qc, p.gameName, p.worldName) });
}

export function useUpdateSession() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: UpsertSessionPayload) => sessionsApi.updateSession(payload), onSuccess: (_d, p) => invalidateSessions(qc, p.gameName, p.worldName) });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { gameName: string; worldName: string; externalId: string }) => sessionsApi.deleteSession(v.gameName, v.worldName, v.externalId),
    onSuccess: (_d, v) => invalidateSessions(qc, v.gameName, v.worldName),
  });
}
